import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CaretLeft, CaretRight, CircleNotch, Warning, UsersThree, CalendarBlank, X, MapPin, CloudRain } from '@phosphor-icons/react';
import { schedulingAPI } from '../../api/api';

const PALETTE = [
  { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-800 dark:text-orange-200', bar: 'bg-[#EA580C]' },
  { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-800 dark:text-blue-200', bar: 'bg-blue-600' },
  { bg: 'bg-emerald-100 dark:bg-emerald-900/40', text: 'text-emerald-800 dark:text-emerald-200', bar: 'bg-emerald-600' },
  { bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-800 dark:text-purple-200', bar: 'bg-purple-600' },
  { bg: 'bg-amber-100 dark:bg-amber-900/40', text: 'text-amber-800 dark:text-amber-200', bar: 'bg-amber-600' },
  { bg: 'bg-teal-100 dark:bg-teal-900/40', text: 'text-teal-800 dark:text-teal-200', bar: 'bg-teal-600' },
];

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const mondayOf = (d) => {
  const dt = new Date(d);
  dt.setDate(dt.getDate() - ((dt.getDay() + 6) % 7));
  return dt.toISOString().slice(0, 10);
};

const shiftWeek = (weekStart, delta) => {
  const dt = new Date(weekStart);
  dt.setDate(dt.getDate() + delta * 7);
  return dt.toISOString().slice(0, 10);
};

const fmtRange = (s, e) => {
  const f = (iso) => new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${f(s)} → ${f(e)}`;
};

const Chip = ({ block, color, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full text-left rounded-md px-1.5 py-1 ${color.bg} ${color.text} ${block.conflict ? 'ring-2 ring-red-500' : ''} hover:opacity-80 transition-opacity`}
    title={`${block.phase_name} — ${block.schedule_title}`}
    data-testid={`calendar-chip-${block.phase_id}`}
  >
    <span className="block text-[10px] font-bold leading-tight truncate">{block.phase_name}</span>
    <span className="block text-[9px] leading-tight truncate opacity-75">{block.schedule_title}</span>
  </button>
);

export const CrewCalendar = () => {
  const [weekStart, setWeekStart] = useState(() => mondayOf(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [rainByDate, setRainByDate] = useState({});

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/weather/job-outlook`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    }).then((res) => setRainByDate(res.data.rain_by_date || {})).catch(() => {});
  }, []);

  const load = useCallback((ws) => {
    setLoading(true);
    schedulingAPI.crewCalendar(ws)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(weekStart); }, [weekStart, load]);

  const scheduleColors = {};
  let ci = 0;
  const colorFor = (sid) => {
    if (!scheduleColors[sid]) scheduleColors[sid] = PALETTE[ci++ % PALETTE.length];
    return scheduleColors[sid];
  };

  const todayIso = new Date().toISOString().slice(0, 10);
  const days = data?.days || [];
  const covers = (b, day) => b.start_date <= day && day <= b.end_date;
  const hasConflicts = (data?.workers || []).some((w) => w.blocks.some((b) => b.conflict));

  const weekLabel = days.length
    ? `${new Date(days[0] + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${new Date(days[6] + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`
    : '';

  return (
    <div className="space-y-4" data-testid="crew-calendar">
      {/* Week nav */}
      <div className="bg-white dark:bg-neutral-800 rounded-xl border-2 border-[#0F172A] dark:border-neutral-700 shadow-[3px_3px_0px_#0F172A] dark:shadow-[3px_3px_0px_#EA580C] p-4 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <UsersThree size={22} weight="fill" className="text-[#EA580C]" />
          <h2 className="font-bold text-lg text-neutral-900 dark:text-white">Crew Calendar</h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setWeekStart((w) => shiftWeek(w, -1))} className="w-9 h-9 rounded-full border-2 border-neutral-200 dark:border-neutral-600 flex items-center justify-center hover:border-[#EA580C] text-neutral-600 dark:text-neutral-300" data-testid="calendar-prev-week">
            <CaretLeft size={16} weight="bold" />
          </button>
          <button onClick={() => setWeekStart(mondayOf(new Date()))} className="h-9 px-4 rounded-full border-2 border-neutral-200 dark:border-neutral-600 text-xs font-bold hover:border-[#EA580C] text-neutral-700 dark:text-neutral-200" data-testid="calendar-today-btn">
            Today
          </button>
          <button onClick={() => setWeekStart((w) => shiftWeek(w, 1))} className="w-9 h-9 rounded-full border-2 border-neutral-200 dark:border-neutral-600 flex items-center justify-center hover:border-[#EA580C] text-neutral-600 dark:text-neutral-300" data-testid="calendar-next-week">
            <CaretRight size={16} weight="bold" />
          </button>
          <span className="text-sm font-bold text-neutral-700 dark:text-neutral-200 ml-1" data-testid="calendar-week-label">{weekLabel}</span>
        </div>
      </div>

      {hasConflicts && (
        <div className="flex items-center gap-2 bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-xl px-4 py-2.5 text-sm font-medium text-red-700 dark:text-red-300" data-testid="calendar-conflict-banner">
          <Warning size={17} weight="fill" /> Some workers are double-booked this week — conflicting blocks are outlined in red.
        </div>
      )}

      {Object.keys(rainByDate).some((d) => days.includes(d)) && (
        <div className="flex items-center gap-2 bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-200 dark:border-blue-800 rounded-xl px-4 py-2.5 text-sm font-medium text-blue-700 dark:text-blue-300" data-testid="calendar-rain-banner">
          <CloudRain size={17} weight="fill" /> Rain is forecast at a job site this week — days marked below. Consider shifting outdoor phases.
        </div>
      )}

      {/* Selected block detail */}
      {selected && (
        <div className="bg-white dark:bg-neutral-800 rounded-xl border-2 border-[#EA580C] p-4 flex items-start justify-between gap-3" data-testid="calendar-block-detail">
          <div className="min-w-0">
            <p className="font-bold text-neutral-900 dark:text-white">{selected.phase_name}</p>
            <p className="text-sm text-neutral-500 truncate">{selected.schedule_title}</p>
            <div className="flex items-center gap-3 mt-1.5 text-xs text-neutral-600 dark:text-neutral-300 flex-wrap">
              <span className="flex items-center gap-1 font-bold"><CalendarBlank size={13} weight="bold" /> {fmtRange(selected.start_date, selected.end_date)}</span>
              {selected.location && <span className="flex items-center gap-1"><MapPin size={13} weight="bold" /> {selected.location}</span>}
              <span className={`font-bold uppercase text-[10px] rounded-full px-2 py-0.5 ${selected.status === 'delayed' ? 'bg-amber-100 text-amber-700' : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-600 dark:text-neutral-300'}`}>{selected.status}</span>
              {selected.conflict && <span className="flex items-center gap-1 text-red-600 font-bold"><Warning size={13} weight="fill" /> Double-booked</span>}
            </div>
          </div>
          <button onClick={() => setSelected(null)} className="w-7 h-7 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-700 flex items-center justify-center text-neutral-400 shrink-0" data-testid="calendar-detail-close">
            <X size={15} weight="bold" />
          </button>
        </div>
      )}

      {/* Grid */}
      <div className="bg-white dark:bg-neutral-800 rounded-xl border-2 border-[#0F172A] dark:border-neutral-700 shadow-[3px_3px_0px_#0F172A] dark:shadow-[3px_3px_0px_#EA580C] overflow-hidden">
        {loading ? (
          <div className="p-16 flex items-center justify-center text-neutral-400">
            <CircleNotch size={28} className="animate-spin" />
          </div>
        ) : !data || (data.workers.length === 0 && data.unassigned.length === 0) ? (
          <div className="p-12 text-center" data-testid="calendar-empty-state">
            <CalendarBlank size={44} weight="duotone" className="text-neutral-300 dark:text-neutral-600 mx-auto mb-2" />
            <p className="font-bold text-neutral-700 dark:text-neutral-200">Nothing scheduled this week</p>
            <p className="text-sm text-neutral-500 mt-1">Phases from your draft & active schedules will appear here once they overlap this week.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse min-w-[760px]">
              <thead>
                <tr className="bg-neutral-50 dark:bg-neutral-900/50">
                  <th className="text-left px-3 py-2.5 text-[11px] font-bold uppercase text-neutral-400 w-40 border-b-2 border-neutral-200 dark:border-neutral-700">Worker</th>
                  {days.map((d, i) => (
                    <th key={d} className={`px-1.5 py-2.5 text-center border-b-2 border-l border-neutral-200 dark:border-neutral-700 ${d === todayIso ? 'bg-orange-50 dark:bg-orange-900/20' : ''}`}>
                      <span className="block text-[10px] font-bold uppercase text-neutral-400">{DAY_LABELS[i]}</span>
                      <span className={`block text-sm font-bold ${d === todayIso ? 'text-[#EA580C]' : 'text-neutral-700 dark:text-neutral-200'}`}>{Number(d.slice(8, 10))}</span>
                      {rainByDate[d] && (
                        <span
                          className="inline-flex items-center gap-0.5 text-[9px] font-bold text-blue-600 dark:text-blue-400 mt-0.5"
                          title={`Rain forecast at: ${rainByDate[d].locations.join(', ')} (${rainByDate[d].max_prob}%)`}
                          data-testid={`calendar-rain-${d}`}
                        >
                          <CloudRain size={11} weight="fill" /> {rainByDate[d].max_prob}%
                        </span>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.workers.map((w) => (
                  <tr key={w.worker_id} className="border-b border-neutral-100 dark:border-neutral-700/60" data-testid={`calendar-row-${w.worker_id}`}>
                    <td className="px-3 py-2 align-top">
                      <p className="font-bold text-sm text-neutral-900 dark:text-white truncate">{w.name}</p>
                      {w.trade && <p className="text-[11px] text-neutral-500 truncate">{w.trade}</p>}
                    </td>
                    {days.map((d) => (
                      <td key={d} className={`px-1 py-1.5 align-top border-l border-neutral-100 dark:border-neutral-700/60 ${d === todayIso ? 'bg-orange-50/60 dark:bg-orange-900/10' : ''}`}>
                        <div className="space-y-1 min-h-[34px]">
                          {w.blocks.filter((b) => covers(b, d)).map((b) => (
                            <Chip key={b.phase_id + d} block={b} color={colorFor(b.schedule_id)} onClick={() => setSelected(b)} />
                          ))}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
                {data.unassigned.length > 0 && (
                  <tr className="bg-red-50/50 dark:bg-red-900/10" data-testid="calendar-unassigned-row">
                    <td className="px-3 py-2 align-top">
                      <p className="font-bold text-sm text-red-600 dark:text-red-400 flex items-center gap-1"><Warning size={13} weight="fill" /> Unassigned</p>
                      <p className="text-[11px] text-neutral-500">Needs crew</p>
                    </td>
                    {days.map((d) => (
                      <td key={d} className="px-1 py-1.5 align-top border-l border-neutral-100 dark:border-neutral-700/60">
                        <div className="space-y-1 min-h-[34px]">
                          {data.unassigned.filter((b) => covers(b, d)).map((b) => (
                            <Chip key={b.phase_id + d} block={{ ...b, conflict: false }} color={{ bg: 'bg-red-100 dark:bg-red-900/40', text: 'text-red-800 dark:text-red-200' }} onClick={() => setSelected(b)} />
                          ))}
                        </div>
                      </td>
                    ))}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Legend */}
      {data && data.workers.length > 0 && (
        <div className="flex items-center gap-3 flex-wrap text-xs text-neutral-500 px-1" data-testid="calendar-legend">
          {Object.entries(scheduleColors).map(([sid, c]) => {
            const title = [...data.workers.flatMap((w) => w.blocks), ...data.unassigned].find((b) => b.schedule_id === sid)?.schedule_title;
            return (
              <span key={sid} className="flex items-center gap-1.5">
                <span className={`w-2.5 h-2.5 rounded-full ${c.bar}`} /> {title}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};
